<template>
  <component :is="getComponent(notice)" v-if="notice" :show-close="true" :notice-detail="notice" />
</template>

<script lang="ts" setup>
  import { onMounted, ref, watch } from 'vue'
  import { NoticeCategory } from '@/core/databus/IMessage'
  import type { NoticeDataType, NoticeListener } from '@/core/Notification/NotificationManager'
  import { INSTANCE as NotificationManager } from '@/core/Notification/NotificationManager'

  import ActionNoticeEmphasized from './ActionNoticeEmphasized.vue'
  import ActionNoticeWeak from './ActionNoticeWeak.vue'
  import NotificationTransmission from './NotificationTransmission.vue'
  import { useSharedActiveMenuItem } from '@/core/compositions/ActiveMenuItem'

  const getComponent = (notice: NoticeDataType) => {
    switch (notice.category) {
      case NoticeCategory.TRANSMISSION:
        return NotificationTransmission

      case NoticeCategory.NOTICE:
        if (notice.isEmphasized) {
          return ActionNoticeEmphasized
        } else {
          return ActionNoticeWeak
        }
      default:
        break
    }
  }

  const notice = ref<NoticeDataType | null>()

  const peekEmphasizedNotice = () => {
    const noticeList: NoticeDataType[] = NotificationManager.getUnreadEmphasizedNotices()
    const [newNotice] = noticeList

    notice.value = newNotice
  }

  const peekNewNotice = (noticeList: NoticeDataType[]) => {
    const [newNotice] = noticeList

    if (!newNotice?.isRead) {
      notice.value = newNotice
    }
  }

  const noticeListener: NoticeListener = {
    onAdded: (noticeList: NoticeDataType[]) => {
      peekNewNotice(noticeList)
    },
    onUpdated: (noticeList: NoticeDataType[]) => {
      peekNewNotice(noticeList)
    },
    onRemoved: () => {
      peekEmphasizedNotice()
    },
    onMarkAsRead() {
      peekEmphasizedNotice()
    },
    onRemoveAll() {
      peekEmphasizedNotice()
    },
    onDismiss() {
      peekEmphasizedNotice()
    },
  }

  const noticeListenerKey = ref('')

  const { isFileManager } = useSharedActiveMenuItem()

  watch(
    isFileManager,
    (currentAppIsFileManager) => {
      if (currentAppIsFileManager) {
        noticeListenerKey.value = NotificationManager.addNoticeListener(noticeListener)
      } else {
        notice.value = undefined
        NotificationManager.removeNoticeListener(noticeListenerKey.value)
      }
    },
    { immediate: true }
  )

  onMounted(() => {
    peekEmphasizedNotice()
  })
  // For unit testing
  defineExpose({
    peekNewNotice,
  })
</script>
<style scoped lang="stylus"></style>
